// Schemat kolumn + walidacja dla tabeli "Magazyn Warszawa - Nowy Handling"
// (tryb "wiele wpisow"). Uzytkownik wpisuje dane wprost w komorki albo wkleja
// zakres z arkusza Excel. Kazda komorka jest walidowana na biezaco.

// Kolejnosc = kolejnosc kolumn w tabeli. Wszystkie pola sa wymagane.
export const MW_NOWY_HANDLING_COLUMNS = [
  { key: "przyjazdAuta", label: "Przyjazd Auta", type: "time" },
  { key: "nrListu", label: "Nr listu", type: "text", exactLength: 14 },
  { key: "nazwaFirmy", label: "Nazwa firmy", type: "company" },
  { key: "startSkanowania", label: "Start skanowania", type: "time" },
];

export const makeEmptyRow = () =>
  MW_NOWY_HANDLING_COLUMNS.reduce((acc, col) => {
    acc[col.key] = "";
    return acc;
  }, {});

// Ilosc "paczki" vs "palety" - przesylka jest ALBO w paczkach ALBO na paletach.
// Gdy jedna grupa ma wartosc > 0, druga jest zerowana i zablokowana do edycji.
export const ILOSC_PACZKI_FIELDS = ["iloscDeklPaczek", "iloscFaktPaczek"];
export const ILOSC_PALETY_FIELDS = ["iloscDeklPalet", "iloscFaktPalet"];

export const iloscGroupActive = (values = []) =>
  values.some((v) => {
    const s = String(v ?? "").trim();
    return s !== "" && Number(s) > 0;
  });

// Normalizacja godziny wklejonej z arkusza: "8:30" -> "08:30", "08:30:00" -> "08:30".
// Wartosci nie pasujace do wzorca zostawiamy bez zmian - walidacja je wychwyci.
const normalizePastedTime = (raw) => {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : s;
};

// Parsuje dane wklejone z arkusza (TSV: kolumny rozdzielone tabulatorem, wiersze
// nowymi liniami) na wiersze tabeli. Kolejnosc kolumn = MW_NOWY_HANDLING_COLUMNS.
// Puste linie sa pomijane. Zwraca row[].
export const parseMwNowyHandlingPaste = (text) => {
  if (!text) return [];
  return String(text)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim().length > 0)
    .map((line) => {
      const cells = line.split("\t");
      const row = makeEmptyRow();
      MW_NOWY_HANDLING_COLUMNS.forEach((col, i) => {
        const raw = (cells[i] ?? "").trim();
        row[col.key] = col.type === "time" ? normalizePastedTime(raw) : raw;
      });
      return row;
    });
};

const NBSP_RE = new RegExp(String.fromCharCode(0x00a0), "g");
const APOSTROPHE_RE = /['‘’`]/g;

// Normalizacja nazwy firmy do porownania ze slownikiem (ta sama regula,
// ktorej powinien uzywac backend przy zapisie).
export const normalizeCompany = (value) =>
  (value == null ? "" : String(value))
    .normalize("NFKC")
    .replace(NBSP_RE, " ")
    .replace(APOSTROPHE_RE, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

// Slownik: znormalizowany klucz -> pisownia kanoniczna z bazy.
export const buildCompanyDict = (companies = []) => {
  const dict = new Map();
  companies.forEach((name) => {
    const key = normalizeCompany(name);
    if (key && !dict.has(key)) dict.set(key, name);
  });
  return dict;
};

const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = curr;
  }
  return prev[b.length];
};

// Najlepsza podpowiedz "czy chodzilo o..." dla wpisu spoza slownika.
export const suggestCompany = (value, dict) => {
  const key = normalizeCompany(value);
  if (!key || !dict || dict.size === 0) return null;
  let best = null;
  let bestDist = Infinity;
  dict.forEach((canonical, dictKey) => {
    if (Math.abs(dictKey.length - key.length) > 4) return;
    const dist = levenshtein(key, dictKey);
    if (dist < bestDist) {
      bestDist = dist;
      best = canonical;
    }
  });
  const limit = Math.max(2, Math.floor(key.length * 0.34));
  return best && bestDist <= limit ? best : null;
};

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Zwraca null (OK) albo { message, suggestion?, fix?, silent? }.
// fix = gotowa wartosc do jednego klikniecia (auto-poprawa).
// silent = auto-snap (np. nazwa firmy rowna po normalizacji) - nie jest bledem.
export const validateCell = (col, rawValue, ctx = {}) => {
  const value = typeof rawValue === "string" ? rawValue.trim() : rawValue == null ? "" : rawValue;

  if (value === "") return { message: "Pole wymagane" };

  switch (col.type) {
    case "time":
      return TIME_RE.test(value)
        ? null
        : { message: "Godzina w formacie HH:MM (np. 08:30)" };

    case "text":
      if (col.exactLength && String(value).length !== col.exactLength) {
        return {
          message: `Wymagane dokładnie ${col.exactLength} znaków (jest ${String(value).length})`,
        };
      }
      return null;

    case "enum":
      return col.options.includes(value)
        ? null
        : { message: `Dozwolone: ${col.options.join(", ")}` };

    case "int":
      return /^\d+$/.test(String(value))
        ? null
        : { message: "Liczba całkowita ≥ 0" };

    case "company": {
      const dict = ctx.companyDict;
      if (!dict || dict.size === 0) return null; // slownik jeszcze sie laduje
      const key = normalizeCompany(value);
      if (dict.has(key)) {
        const canonical = dict.get(key);
        return canonical !== value ? { fix: canonical, silent: true } : null;
      }
      const suggestion = suggestCompany(value, dict);
      return {
        message: "Brak w słowniku firm Magazyn Warszawa",
        suggestion,
        fix: suggestion || undefined,
      };
    }

    default:
      return null;
  }
};

// Walidacja miedzy polami w obrebie jednego wiersza (godziny sa tekstem "HH:MM"
// 24h zero-padded, wiec porownanie leksykalne == chronologiczne). Pusta wartosc
// po ktorejkolwiek stronie => brak ograniczenia. Zwraca { "<colKey>": { message } }.
export const validateRowCrossField = (row) => {
  const out = {};
  const przyjazd = String(row?.przyjazdAuta ?? "").trim();
  const start = String(row?.startSkanowania ?? "").trim();
  const koniec = String(row?.zakonczenieSkanowania ?? "").trim();

  if (przyjazd && start && start < przyjazd) {
    out.startSkanowania = {
      message: "Start skanowania nie może być wcześniejszy niż przyjazd auta",
    };
  }
  if (start && koniec && koniec < start) {
    out.zakonczenieSkanowania = {
      message:
        "Zakończenie skanowania nie może być wcześniejsze niż start skanowania",
    };
  }
  return out;
};

// Waliduje cala tabele. Zwraca:
//   errorsByCell: { "<rowIdx>:<colKey>": { message, suggestion, fix } }
//   autoFixes:    [{ rowIdx, colKey, value }]  (auto-snap nazwy firmy)
//   errorCount:   liczba komorek z realnym bledem (bez cichych auto-snapow)
export const validateRows = (rows, ctx = {}) => {
  const errorsByCell = {};
  const autoFixes = [];
  let errorCount = 0;

  rows.forEach((row, rowIdx) => {
    MW_NOWY_HANDLING_COLUMNS.forEach((col) => {
      const res = validateCell(col, row[col.key], ctx);
      if (!res) return;
      if (res.silent && res.fix != null) {
        autoFixes.push({ rowIdx, colKey: col.key, value: res.fix });
        return;
      }
      errorsByCell[`${rowIdx}:${col.key}`] = {
        message: res.message,
        suggestion: res.suggestion,
        fix: res.fix,
      };
      errorCount += 1;
    });

    const cross = validateRowCrossField(row);
    Object.entries(cross).forEach(([colKey, res]) => {
      const cellKey = `${rowIdx}:${colKey}`;
      if (!errorsByCell[cellKey]) {
        errorsByCell[cellKey] = { message: res.message };
        errorCount += 1;
      }
    });
  });

  return { errorsByCell, autoFixes, errorCount };
};
